import { QuestionText } from 'utils/questionMarkdown';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Question, QuestionOption, ResponsePayload } from '../../../types';
import { HorizontalDragDrop } from '../../questions/HorizontalDragDrop';
import type { HorizontalDragDropItem, HorizontalDragDropState } from '../../questions/HorizontalDragDrop';
import { registerQuestionRenderer } from './registry';
import type { QuestionRendererComponent } from './registry';

interface DragItem {
  id: number;
  text: string;
  answer?: 'left' | 'right';
}

interface HorizontalDragDropInitialState {
  left: DragItem[];
  middle: DragItem[];
  right: DragItem[];
}

const getItemText = (option: QuestionOption): string => {
  const frontText = typeof option.front_text === 'string' ? option.front_text.trim() : '';
  const rawText = typeof (option as { text?: string }).text === 'string' ? ((option as { text?: string }).text ?? '').trim() : '';
  const backText = typeof option.back_text === 'string' ? option.back_text.trim() : '';
  return frontText || rawText || backText || '';
};

const buildItems = (question: Question): HorizontalDragDropItem[] => {
  const options = Array.isArray(question?.options) ? question.options : [];
  return options
    .filter((option): option is QuestionOption => typeof option?.id === 'number')
    .map((option) => {
      const normalizedBackText = (option.back_text ?? '').toString().trim().toLowerCase();
      return {
        id: option.id,
        text: getItemText(option),
        answer: normalizedBackText === 'right' ? 'right' : 'left',
      };
    });
};

const buildCorrectColumnMap = (question: Question): Record<number, 'left' | 'right'> => {
  const options = Array.isArray(question?.options) ? question.options : [];
  return options.reduce<Record<number, 'left' | 'right'>>((acc, option) => {
    if (typeof option?.id !== 'number') {
      return acc;
    }
    const normalizedBackText = (option.back_text ?? '').toString().trim().toLowerCase();
    acc[option.id] = normalizedBackText === 'right' ? 'right' : 'left';
    return acc;
  }, {});
};

const shuffleItems = <T,>(items: T[]): T[] => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
};

export const buildHorizontalDragDropInitialState = (
  items: DragItem[],
  selectedOptionIds: number[],
  correctColumnById: Record<number, 'left' | 'right'>,
): HorizontalDragDropInitialState => {
  const middle = [...items];
  const left: DragItem[] = [];
  const right: DragItem[] = [];

  selectedOptionIds.forEach((id) => {
    const index = middle.findIndex((item) => item.id === id);
    if (index === -1) {
      return;
    }

    const [item] = middle.splice(index, 1);
    const target = correctColumnById[item.id];

    if (target === 'right') {
      right.push(item);
      return;
    }

    left.push(item);
  });

  return {
    left,
    middle: shuffleItems(middle),
    right,
  };
};

export const HorizontalDragDropRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const items = useMemo(() => buildItems(question), [question]);
  const itemTextById = useMemo(() => new Map(items.map((item) => [item.id, item.text])), [items]);
  const correctColumnById = useMemo(() => buildCorrectColumnMap(question), [question]);
  const selectedOptionIds = useMemo(
    () => (Array.isArray(existingResponse?.selected_options) ? existingResponse!.selected_options! : []),
    [existingResponse],
  );

  const initialState = useMemo(() => {
    return buildHorizontalDragDropInitialState(items, selectedOptionIds, correctColumnById);
  }, [items, selectedOptionIds, correctColumnById]);

  const [shuffledItems, setShuffledItems] = useState<HorizontalDragDropItem[]>(
    () => [...initialState.middle, ...initialState.left, ...initialState.right] as HorizontalDragDropItem[],
  );

  const emitIncomplete = useCallback(
    (state: HorizontalDragDropState) => {
      onExtrasChange?.({
        horizontalState: state,
      });
      onHasUserInputChange?.(false);
      onValidityChange?.(false);
      onDraftChange(existingResponse ?? null);
    },
    [existingResponse, onDraftChange, onExtrasChange, onHasUserInputChange, onValidityChange],
  );

  const emitComplete = useCallback(
    (state: HorizontalDragDropState) => {
      const { leftIds, rightIds } = state;
      onExtrasChange?.({
        horizontalState: state,
      });
      onHasUserInputChange?.(true);
      onValidityChange?.(true);

      const orderedIds = [...leftIds, ...rightIds];
      const textValue = orderedIds
        .map((id) => itemTextById.get(id) ?? '')
        .filter((text) => text.trim().length > 0)
        .join('; ');

      const payload: ResponsePayload = {
        question: questionId,
        selected_options: orderedIds,
      };

      if (textValue.length > 0) {
        payload.text_value = textValue;
      }

      onDraftChange(payload);
    },
    [itemTextById, onDraftChange, onExtrasChange, onHasUserInputChange, onValidityChange, questionId],
  );

  const initialStateRef = useRef(initialState);
  initialStateRef.current = initialState;

  // Only reset drag state when the question itself changes, not when
  // existingResponse updates after an auto-save. Reacting to autosave
  // updates can make items snap back while the learner is interacting.
  useEffect(() => {
    const state = initialStateRef.current;
    setShuffledItems([...state.middle, ...state.left, ...state.right] as HorizontalDragDropItem[]);
  }, [questionId]);

  const handleProgressChange = useCallback((state: HorizontalDragDropState, isComplete: boolean) => {
    if (isComplete) {
      emitComplete(state);
      return;
    }

    emitIncomplete(state);
  }, [emitComplete, emitIncomplete]);

  return (
    <HorizontalDragDrop
      items={shuffledItems}
      leftTitle={question.low_text ? <QuestionText text={question.low_text} citations={question.citations} inline links={false} /> : question.low_text}
      rightTitle={question.high_text ? <QuestionText text={question.high_text} citations={question.citations} inline links={false} /> : question.high_text}
      initialSelectedIds={selectedOptionIds}
      disabled={isSubmitting}
      onProgressChange={handleProgressChange}
    />
  );
};

registerQuestionRenderer(['horizontal_drag_drop'], HorizontalDragDropRenderer);
